import React from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  ButtonSecondary,
  IconDownload,
  LoadingIndicator,
  spacer24,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { DEFAULT_LOCALE } from '@spotify-internal/s4mp-shared';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { useUnassociatedVideosExportRequest } from '@hooks/useUnassociatedVideosExportRequest';
import {
  buildAddSnackbarAction,
  SnackBarColorSet,
} from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import {
  ButtonContainer,
  LoadingIndicatorContainer,
} from '@components/Catalog/ContentArea/CatalogExports/styles';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { CreateUnassociatedVideoExportRequest__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { UnassociatedVideosTable } from './UnassociatedVideosTable/UnassociatedVideosTable';

interface UnassociatedVideosProps {}

const UnassociatedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer24};
`;

export const UnassociatedVideos: React.VFC<UnassociatedVideosProps> = ({}) => {
  const t = useT();

  const [, dispatchSnackbar] = useSnackbarNotifications();

  const {
    status,
    actions: { s4mpUnassociatedVideoExport },
  } = useUnassociatedVideosExportRequest();

  const { locale } = useRouter();
  const handleUnassociatedExport = async () => {
    // This form is primarily using Formik for field validation
    // The values submitted to the BE come from state, excluding exportFileName
    const body: CreateUnassociatedVideoExportRequest__Output = {
      languageLocale: locale ? locale : DEFAULT_LOCALE,
    };

    await s4mpUnassociatedVideoExport(body).then(res => {
      if (!res) {
        sendEvent({
          eventCategory: EVENT_CATEGORY.UNASSOCIATED_VIDEOS_REPORT,
          eventAction: EVENT_ACTION.CLICK,
          eventLabel:
            'Error occurred requesting to create unassociated videos report',
          product: GA_PRODUCT_CATALOG,
        });
        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.unassociated_videos_report.snackbars.creating_report_error',
              `We couldn't create your report. Try again.`,
              'Snackbar text that indicates an error occurred with the report',
            ),
            SnackBarColorSet.NEGATIVE,
          ),
        );
      } else {
        sendEvent({
          eventCategory: EVENT_CATEGORY.UNASSOCIATED_VIDEOS_REPORT,
          eventAction: EVENT_ACTION.CLICK,
          eventLabel: 'Submitted to create unassociated videos report',
          product: GA_PRODUCT_CATALOG,
        });
        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.unassociated_videos_report.snackbars.creating_report',
              `Creating report. You can leave this page. You'll get an email when it's ready.`,
              'Snackbar text that indicates an unassociated video report is being created',
            ),
          ),
        );
      }
    });
  };

  return (
    <UnassociatedContainer>
      <Stack
        direction="horizontal"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <div>
          <Type as="h2" variant="canon" paddingBottom={spacer8}>
            {t(
              'atlas.unassociated_videos',
              'Unassociated videos',
              'Title for Unassociated videos section',
            )}
          </Type>
          <Type
            as="p"
            dangerouslySetInnerHTML={{
              __html: t(
                'atlas.actions.export_unassociated_description',
                'These videos aren’t associated with any audio tracks and won’t be discoverable on Spotify. Review the reasons given and redeliver.',
                'Text explaining what the unassociated export button is for',
              ),
            }}
          />
        </div>
        <ButtonContainer>
          {status === FETCH_STATUS.FETCHING ? (
            <LoadingIndicatorContainer>
              <LoadingIndicator indicatorSize="md" />
            </LoadingIndicatorContainer>
          ) : (
            <ButtonSecondary
              iconLeading={IconDownload}
              size="small"
              type="submit"
              onClick={() => {
                handleUnassociatedExport();
              }}
            >
              {t(
                'atlas.labels.export',
                'Export',
                'Button text to create export',
              )}
            </ButtonSecondary>
          )}
        </ButtonContainer>
      </Stack>
      <UnassociatedVideosTable />
    </UnassociatedContainer>
  );
};
